import { addQueryString } from '@umu/umu-dom';
import { isIos, isUMUWebview } from '@umu/umu-ua';
import umuWebview from '@umu/umu-webview';

interface PdfTextItem {
    str: string;
    transform: number[];
    width?: number;
    height?: number;
}

export class AnnotationsLayer {
    constructor(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
        public pdfjsLib: any,
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        public page: any,
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js viewport object
        public viewport: any,
        public pdfRenderContainer: HTMLElement | null, // canvas,
        public annotationLayer: HTMLDivElement | null, // 注解层hml容器
        public scale = 1,
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
        public pdfDoc?: any,
        public onInternalLinkClick?: (pageNum: number) => void
    ) {
        this.setContainerPosition();
        this.setupAnnotations();
    }

    setContainerPosition = () => {
        if (!(this.pdfRenderContainer && this.annotationLayer)) {
            return;
        }
        // 使用 offsetWidth/offsetHeight（布局尺寸）而非 getBoundingClientRect()（视觉尺寸）。
        // getBoundingClientRect 包含父元素 CSS transform 的缩放效果：当 .pdf-page-container 有
        // scale() transform 时，返回值会被放大，导致注解层比画布宽，产生右侧多余滚动区域。
        const el = this.pdfRenderContainer;
        this.annotationLayer.style.position = 'absolute';
        // pdf-page-container 保持 width:fit-content，宽度 = canvas 宽度，
        // canvas offsetLeft 始终为 0，无需读取 offsetLeft 做偏移修正。
        this.annotationLayer.style.left = '0px';
        this.annotationLayer.style.top = '0px';
        this.annotationLayer.style.height = `${el.offsetHeight}px`;
        this.annotationLayer.style.width = `${el.offsetWidth}px`;
    };

    setupAnnotations = () => {
        if (!(this.pdfRenderContainer && this.annotationLayer)) {
            return;
        }

        this.clearLayer();

        // biome-ignore lint/suspicious/noExplicitAny: PDF.js annotations array
        const annotationsPromise = this.page.getAnnotations().then((annotationsData: any) => {
            this.viewport = this.viewport.clone({
                dontFlip: true,
            });

            for (let i = 0; i < annotationsData.length; i++) {
                const data = annotationsData[i];
                // 只处理 Link 类型注解
                if (data.subtype !== 'Link') {
                    continue;
                }

                // 跳过 rect 字段缺失或格式异常的注解，防止 normalizeRect 抛出 TypeError
                if (!Array.isArray(data.rect) || data.rect.length < 4) {
                    continue;
                }

                let element: HTMLElement;
                // data.url 是经 PDF.js 安全校验的 URL；部分 PDF 工具生成的链接仅有
                // unsafeUrl，需回退读取（仅接受 http/https 协议）。
                const externalUrl = data.url || (/^https?:\/\//i.test(data.unsafeUrl ?? '') ? data.unsafeUrl : null);
                if (externalUrl) {
                    element = this.createA(externalUrl);
                } else if (data.dest && this.pdfDoc && this.onInternalLinkClick) {
                    element = this.createInternalLink(data.dest);
                } else {
                    continue;
                }

                const rect = data.rect;
                const view = this.page.view;
                const renderedRect = this.pdfjsLib.Util.normalizeRect([
                    rect[0],
                    view[3] - rect[1] + view[1],
                    rect[2],
                    view[3] - rect[3] + view[1],
                ]);
                element.style.position = 'absolute';
                element.style.left = `${renderedRect[0] * this.scale}px`;
                element.style.top = `${renderedRect[1] * this.scale}px`;
                element.style.height = `${(renderedRect[3] - renderedRect[1]) * this.scale}px`;
                element.style.width = `${(renderedRect[2] - renderedRect[0]) * this.scale}px`;
                this.annotationLayer?.append(element);
            }
        }).catch(() => { /* getAnnotations 不可用或注解解析异常时静默跳过 */ });

        // 扫描文本内容中的 URL（补充 PDF 注解未覆盖的文本链接）
        const textPromise = this.page
            .getTextContent()
            .then((textContent: { items: PdfTextItem[] }) => this.appendTextUrls(textContent.items || []))
            .catch(() => { /* getTextContent 不可用时静默跳过 */ });

        return Promise.all([annotationsPromise, textPromise]);
    };

    // URL 可能被 pdfjs 拆分到多个 item，需先按行合并再整体匹配
    private appendTextUrls = (rawItems: PdfTextItem[]) => {
        const view: number[] = this.page.view;
        const pageHeight = view[3] - view[1];
        const items = rawItems.filter((it) => it.str && Array.isArray(it.transform));
        const lines = this.groupItemsByLine(items);
        const URL_RE = /https?:\/\/\S+/g;
        for (const line of lines) {
            const lineText = line.map((it) => it.str).join('');
            URL_RE.lastIndex = 0;
            let urlMatch = URL_RE.exec(lineText);
            while (urlMatch !== null) {
                const url = urlMatch[0].replace(/[.,:;!?)>\]"']+$/, '');
                if (url.length >= 8) {
                    this.appendUrlElement(url, urlMatch.index, line, view, pageHeight);
                }
                urlMatch = URL_RE.exec(lineText);
            }
        }
    };

    private groupItemsByLine = (items: PdfTextItem[]): PdfTextItem[][] => {
        const LINE_GAP = 4;
        const sorted = [...items].sort(
            (a, b) => b.transform[5] - a.transform[5] || a.transform[4] - b.transform[4],
        );
        const lines: PdfTextItem[][] = [];
        for (const item of sorted) {
            const y = item.transform[5];
            const last = lines[lines.length - 1];
            if (last && Math.abs(last[0].transform[5] - y) <= LINE_GAP) {
                last.push(item);
            } else {
                lines.push([item]);
            }
        }
        return lines;
    };

    private appendUrlElement = (
        url: string,
        urlStart: number,
        line: PdfTextItem[],
        view: number[],
        pageHeight: number,
    ) => {
        const urlEnd = urlStart + url.length;
        let charPos = 0;
        let minX = Number.POSITIVE_INFINITY;
        let maxX = 0;
        let pdfY = 0;
        let itemH = 0;
        for (const it of line) {
            const s = charPos;
            const e = charPos + it.str.length;
            if (e > urlStart && s < urlEnd) {
                const x = it.transform[4];
                const w = it.width ?? 0;
                if (x < minX) minX = x;
                if (x + w > maxX) maxX = x + w;
                pdfY = it.transform[5];
                itemH = it.height || Math.abs(it.transform[0]) || 12;
            }
            charPos += it.str.length;
        }
        if (minX === Number.POSITIVE_INFINITY) return;
        const element = this.createA(url);
        element.style.position = 'absolute';
        element.style.left = `${(minX - view[0]) * this.scale}px`;
        element.style.top = `${(pageHeight - (pdfY - view[1]) - itemH) * this.scale}px`;
        element.style.width = `${(maxX - minX) * this.scale}px`;
        element.style.height = `${itemH * this.scale}px`;
        this.annotationLayer?.append(element);
    };

    private clearLayer = () => {
        if (!this.annotationLayer) {
            return;
        }
        this.annotationLayer.innerHTML = '';
    };

    private createA = (initialUrl = '#') => {
        const a = document.createElement('a') as HTMLAnchorElement;
        a.className = 'pdf-annotation-a';

        let url = initialUrl;
        if (isUMUWebview && url !== '#') {
            if (window.location.hash) {
                url = addQueryString(url, {
                    share: '0',
                });
            } else {
                url += '#share=0';
            }
        }

        if (isIos && isUMUWebview) {
            a.href = 'javascript:void(0)';
            a.onclick = () => {
                umuWebview.api.exitFullscreen();
                setTimeout(() => {
                    window.open(url, '_blank');
                }, 100);
            };
        } else {
            a.href = url;
            a.target = '_blank';
        }
        return a;
    };

    // biome-ignore lint/suspicious/noExplicitAny: PDF.js dest type
    private createInternalLink = (dest: any): HTMLElement => {
        const btn = document.createElement('a');
        btn.className = 'pdf-annotation-a pdf-annotation-internal';
        btn.href = 'javascript:void(0)';
        btn.onclick = (e) => {
            e.preventDefault();
            this.resolveDestToPage(dest)
                .then((pageNum) => {
                    if (pageNum > 0) this.onInternalLinkClick?.(pageNum);
                })
                .catch(() => {
                    // 静默处理解析失败
                });
        };
        return btn;
    };

    /** 将 PDF dest（字符串命名目标或数组显式目标）解析为 1-based 页码 */
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js dest type
    private resolveDestToPage = async (dest: any): Promise<number> => {
        if (!this.pdfDoc) return -1;
        try {
            // biome-ignore lint/suspicious/noExplicitAny: PDF.js dest array
            const resolved: any[] | null = typeof dest === 'string' ? await this.pdfDoc.getDestination(dest) : dest;
            if (!resolved || resolved.length === 0) return -1;
            const pageIndex: number = await this.pdfDoc.getPageIndex(resolved[0]);
            return pageIndex + 1;
        } catch {
            return -1;
        }
    };
}
